import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule, MatSelectionList } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ReferralCommunicationType } from '@principle-theorem/principle-core/interfaces';
import { NgCommonModule } from '@principle-theorem/ng-shared';

interface ICommunicationTypeOption {
  type: ReferralCommunicationType;
  label: string;
  icon: string;
}

@Component({
  selector: 'pr-referral-communication-type-filter',
  imports: [
    NgCommonModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    MatBadgeModule,
    MatListModule,
  ],
  templateUrl: './referral-communication-type-filter.component.html',
  styleUrl: './referral-communication-type-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralCommunicationTypeFilterComponent {
  selectedTypes = input<ReferralCommunicationType[]>([]);
  disabled = input(false);
  showNotes = input(false);
  showInteractions = input(false);
  selectionChange = output<ReferralCommunicationType[]>();

  readonly selectedFilters = signal<ReferralCommunicationType[]>([]);
  readonly selectionList =
    viewChild.required<MatSelectionList>('selectionList');
  readonly showClearButton = computed(() => this.selectedFilters().length > 0);
  readonly selectedFiltersCount = computed(() => this.selectedFilters().length);

  readonly baseCommunicationTypeOptions: ICommunicationTypeOption[] = [
    {
      type: ReferralCommunicationType.Message,
      label: 'Messages',
      icon: 'message',
    },
    {
      type: ReferralCommunicationType.Media,
      label: 'Media',
      icon: 'photo_library',
    },
    {
      type: ReferralCommunicationType.ClinicalNote,
      label: 'Clinical Notes',
      icon: 'note_add',
    },
  ];

  readonly communicationTypeOptions = computed(() => {
    const options = [...this.baseCommunicationTypeOptions];
    if (this.showInteractions()) {
      options.push({
        type: ReferralCommunicationType.Interaction,
        label: 'Interactions',
        icon: 'swap_horiz',
      });
    }
    if (this.showNotes()) {
      options.push({
        type: ReferralCommunicationType.Note,
        label: 'Notes',
        icon: 'text_snippet',
      });
    }
    return options;
  });

  constructor() {
    afterRenderEffect(() => this._syncSelectionWithInput());
  }

  onSelectionChange(): void {
    const list = this.selectionList();
    if (!list) {
      return;
    }

    const selected = list.selectedOptions.selected.map(
      (option) => option.value as ReferralCommunicationType
    );
    this.selectedFilters.set(selected);
    this.selectionChange.emit(selected);
  }

  clearFilters(): void {
    const list = this.selectionList();
    if (list) {
      list.deselectAll();
    }
    this.selectedFilters.set([]);
    this.selectionChange.emit([]);
  }

  private _syncSelectionWithInput(): void {
    const inputTypes = this.selectedTypes();
    const list = this.selectionList();

    if (!list || !list.options) {
      return;
    }

    if (inputTypes.length === 0) {
      list.deselectAll();
      this.selectedFilters.set([]);
      return;
    }

    this.selectedFilters.set(inputTypes);

    list.deselectAll();
    inputTypes.forEach((type) => {
      const option = list.options.find((opt) => opt.value === type);
      if (option) {
        option.selected = true;
      }
    });
  }
}
